/**
 * ListingForm — create or edit a listing
 *
 * Client component with react-hook-form + Zod validation.
 * Integrates AI-powered image analysis via Gemini Flash to auto-fill fields.
 * Handles image upload, form state, validation, and API submission.
 */

"use client";

import { useCallback, useRef, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    createListingBaseSchema,
    type CreateListingInput,
} from "@/lib/validations/listing";
import { LISTING_TYPES, ITEM_CONDITIONS, DEFAULT_CATEGORIES } from "@/lib/constants";
import { ImageUpload, type AnalysisResult, type ImageUploadHandle } from "@/components/image-upload";
import { Sparkles } from "lucide-react";

interface ListingFormProps {
    initialData?: Partial<CreateListingInput> & { id?: string; slug?: string; imageUrls?: string[] };
}

export function ListingForm({ initialData }: ListingFormProps) {
    const router = useRouter();
    const [isPending, startTransition] = useTransition();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imageUrls, setImageUrls] = useState<string[]>(initialData?.imageUrls || []);
    const [aiAnalysis, setAiAnalysis] = useState<AnalysisResult | null>(null);
    const imageUploadRef = useRef<ImageUploadHandle>(null);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm<CreateListingInput>({
        resolver: zodResolver(createListingBaseSchema),
        defaultValues: {
            type: "DONATION",
            condition: "GOOD",
            ...initialData,
        },
    });

    const selectedType = watch("type");
    const selectedCondition = watch("condition");
    const selectedCategory = watch("categoryId") ?? "";

    /** Apply AI analysis results to form fields */
    const handleAnalysis = useCallback(
        (result: AnalysisResult) => {
            setAiAnalysis(result);

            if (result.title) {
                setValue("title", result.title, { shouldValidate: true });
            }
            if (result.description) {
                // Determine if we should append brand/weight details if they aren't already included
                let description = result.description;
                const details = [];
                if (result.brand && result.brand !== "Inconnue") details.push(`Marque: ${result.brand}`);
                if (result.estimatedWeight) details.push(`Poids estimé: ${result.estimatedWeight}`);

                if (details.length > 0) {
                    description += `\n\n(${details.join(" - ")})`;
                }

                setValue("description", description, { shouldValidate: true });
            }
            if (result.condition) {
                setValue("condition", result.condition as CreateListingInput["condition"], {
                    shouldValidate: true,
                });
            }
            if (result.categorySlug) {
                setValue("categoryId", result.categorySlug, { shouldValidate: true });
            }
            if (result.suggestedType) {
                setValue("type", result.suggestedType as CreateListingInput["type"]);
            }
            if (result.suggestedPrice && result.suggestedType === "SALE") {
                setValue("price", result.suggestedPrice, { shouldValidate: true });
            }
        },
        [setValue],
    );

    /** Track uploaded image URLs */
    const handleImagesChange = useCallback((urls: string[]) => {
        setImageUrls(urls);
    }, []);

    async function onSubmit(data: CreateListingInput) {
        setIsSubmitting(true);

        try {
            // Upload pending images before submitting
            let finalImageUrls = imageUrls;
            if (imageUploadRef.current?.hasImages()) {
                try {
                    finalImageUrls = await imageUploadRef.current.uploadImages();
                } catch {
                    toast.error("Erreur lors de l'envoi des photos");
                    return;
                }
            }

            const isEdit = !!initialData?.id;
            const url = isEdit ? `/api/listings/${initialData.id}` : "/api/listings";
            const method = isEdit ? "PATCH" : "POST";

            const response = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...data, imageUrls: finalImageUrls }),
            });

            if (!response.ok) {
                const err = await response.json();
                toast.error(err.error ?? "Erreur lors de l'enregistrement");
                return;
            }

            const listing = await response.json();
            toast.success(isEdit ? "Annonce modifiée !" : "Annonce publiée avec succès !");

            startTransition(() => {
                router.push(`/annonces/${listing.slug}`);
            });
        } catch {
            toast.error("Erreur réseau — vérifiez votre connexion");
        } finally {
            setIsSubmitting(false);
        }
    }

    const disabled = isSubmitting || isPending;

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className="mt-8 space-y-6"
            noValidate
        >
            {/* Image Upload + AI Analysis */}
            <div className="space-y-2">
                <Label>Photos du matériel</Label>
                <div className="rounded-lg border bg-muted/30 p-4">
                    <p className="mb-4 text-sm text-muted-foreground">
                        <Sparkles className="mr-2 inline-block size-4 text-hp-pink" />
                        Chargez une photo, l'IA remplira le formulaire automatiquement !
                    </p>
                    <ImageUpload
                        ref={imageUploadRef}
                        onAnalysis={handleAnalysis}
                        onImagesChange={handleImagesChange}
                        maxImages={5}
                        disabled={disabled}
                    />
                </div>
            </div>

            {/* AI Suggestion Box */}
            {aiAnalysis && (
                <div className="rounded-md border border-hp-teal/20 bg-hp-teal/5 p-4 text-sm animate-in fade-in slide-in-from-top-2">
                    <h4 className="mb-2 font-medium text-hp-teal flex items-center gap-2">
                        <Sparkles className="size-4" /> Suggestions de l&apos;IA
                    </h4>
                    <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                        <li><strong>Prix suggéré :</strong> {aiAnalysis.suggestedPrice ? `${aiAnalysis.suggestedPrice}€ (Occasion)` : "Gratuit (Don/Prêt)"}</li>
                        <li><strong>Marque :</strong> {aiAnalysis.brand || "Non détectée"}</li>
                        <li><strong>Poids estimé :</strong> {aiAnalysis.estimatedWeight ? aiAnalysis.estimatedWeight : "Non estimé"}</li>
                        <li><strong>Type conseillé :</strong> {LISTING_TYPES[aiAnalysis.suggestedType as keyof typeof LISTING_TYPES]?.label || aiAnalysis.suggestedType}</li>
                    </ul>
                </div>
            )}

            {/* Title */}
            <div className="space-y-2">
                <Label htmlFor="title">Titre de l&apos;annonce *</Label>
                <Input
                    id="title"
                    placeholder="Ex: Fauteuil roulant électrique Invacare"
                    {...register("title")}
                    aria-invalid={!!errors.title}
                    aria-describedby={errors.title ? "title-error" : undefined}
                />
                {errors.title && (
                    <p id="title-error" className="text-sm text-destructive">
                        {errors.title.message}
                    </p>
                )}
            </div>

            {/* Description */}
            <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                    id="description"
                    placeholder="Décrivez l'état, les dimensions, les accessoires inclus..."
                    rows={5}
                    {...register("description")}
                    aria-invalid={!!errors.description}
                    aria-describedby={
                        errors.description ? "description-error" : undefined
                    }
                />
                {errors.description && (
                    <p id="description-error" className="text-sm text-destructive">
                        {errors.description.message}
                    </p>
                )}
            </div>

            {/* Type + Condition row */}
            <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor="type">Type d&apos;annonce *</Label>
                    <Select
                        value={selectedType}
                        onValueChange={(val) =>
                            setValue("type", val as CreateListingInput["type"])
                        }
                    >
                        <SelectTrigger id="type" aria-label="Type d'annonce">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {Object.entries(LISTING_TYPES).map(([key, type]) => (
                                <SelectItem key={key} value={key}>
                                    {type.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="condition">État du matériel *</Label>
                    <Select
                        value={selectedCondition}
                        onValueChange={(val) =>
                            setValue("condition", val as CreateListingInput["condition"])
                        }
                    >
                        <SelectTrigger id="condition" aria-label="État du matériel">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {Object.entries(ITEM_CONDITIONS).map(([key, cond]) => (
                                <SelectItem key={key} value={key}>
                                    {cond.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Price (visible only for SALE) */}
            {selectedType === "SALE" && (
                <div className="space-y-2">
                    <Label htmlFor="price">Prix (€) *</Label>
                    <Input
                        id="price"
                        type="number"
                        min={0}
                        step={1}
                        placeholder="0"
                        {...register("price", { valueAsNumber: true })}
                        aria-invalid={!!errors.price}
                        aria-describedby={errors.price ? "price-error" : undefined}
                    />
                    {errors.price && (
                        <p id="price-error" className="text-sm text-destructive">
                            {errors.price.message}
                        </p>
                    )}
                </div>
            )}

            {/* Deposit (visible for LOAN) */}
            {selectedType === "LOAN" && (
                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                        <Label htmlFor="deposit">Caution (€)</Label>
                        <Input
                            id="deposit"
                            type="number"
                            min={0}
                            step={1}
                            placeholder="0"
                            {...register("deposit", { valueAsNumber: true })}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="loanDuration">Durée du prêt</Label>
                        <Input
                            id="loanDuration"
                            placeholder="Ex: 3 mois, 1 semaine"
                            {...register("loanDuration")}
                        />
                    </div>
                </div>
            )}

            {/* Category */}
            <div className="space-y-2">
                <Label htmlFor="categoryId">Catégorie *</Label>
                <Select
                    value={selectedCategory}
                    onValueChange={(val) => setValue("categoryId", val)}
                >
                    <SelectTrigger id="categoryId" aria-label="Catégorie de matériel">
                        <SelectValue placeholder="Sélectionnez une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                        {DEFAULT_CATEGORIES.map((cat) => (
                            <SelectItem key={cat.slug} value={cat.slug}>
                                {cat.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.categoryId && (
                    <p className="text-sm text-destructive">
                        {errors.categoryId.message}
                    </p>
                )}
            </div>

            {/* Location */}
            <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor="city">Ville *</Label>
                    <Input
                        id="city"
                        placeholder="Paris"
                        {...register("city")}
                        aria-invalid={!!errors.city}
                        aria-describedby={errors.city ? "city-error" : undefined}
                    />
                    {errors.city && (
                        <p id="city-error" className="text-sm text-destructive">
                            {errors.city.message}
                        </p>
                    )}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="postalCode">Code postal *</Label>
                    <Input
                        id="postalCode"
                        placeholder="75001"
                        maxLength={5}
                        {...register("postalCode")}
                        aria-invalid={!!errors.postalCode}
                        aria-describedby={
                            errors.postalCode ? "postalCode-error" : undefined
                        }
                    />
                    {errors.postalCode && (
                        <p id="postalCode-error" className="text-sm text-destructive">
                            {errors.postalCode.message}
                        </p>
                    )}
                </div>
            </div>

            {/* Submit */}
            <Button
                type="submit"
                size="lg"
                className="w-full bg-gradient-to-r from-hp-purple to-hp-orange text-white shadow-hp hover:from-hp-purple-dark hover:to-hp-orange-dark hover:shadow-hp-hover rounded-xl"
                disabled={disabled}
            >
                {disabled ? (
                    <>
                        <Loader2
                            className="mr-2 h-4 w-4 animate-spin"
                            aria-hidden="true"
                        />
                        Publication en cours...
                    </>
                ) : (
                    "Publier l\u2019annonce"
                )}
            </Button>
        </form>
    );
}
